// Приводит HTML, приходящий с бэкенда (описание проекта, комментарии, ссылки),
// к безопасному и единообразному виду перед выводом через v-html:
//  - вырезает потенциально опасные узлы и inline-обработчики (защита от XSS);
//  - убирает презентационные атрибуты (style/class/align/width/…), чтобы вид
//    задавался нашим CSS, а не разнородной вёрсткой бэкенда;
//  - убирает декоративные стрелки-каретки (элементы и символы);
//  - разворачивает <font> и делает внешние ссылки безопасными.

const CARET_GLYPHS = /[▾▼▽⌄˅]/g;

// атрибуты, которые сохраняем; всё остальное срезаем
const KEEP_ATTRS = new Set([
  "href",
  "target",
  "rel",
  "src",
  "alt",
  "colspan",
  "rowspan",
]);

export function sanitizeRichHtml(raw?: string | null): string {
  if (!raw) {
    return "";
  }
  // на случай нестандартного окружения без DOM
  if (typeof window === "undefined" || typeof DOMParser === "undefined") {
    return raw;
  }

  try {
    const doc = new DOMParser().parseFromString(raw, "text/html");
    const body = doc.body;

    // опасные / бесполезные элементы
    body
      .querySelectorAll("script, style, meta, link, iframe, object, embed")
      .forEach((el) => el.remove());

    // каретки-элементы (bootstrap .dropdown-toggle::after, иконки, span.caret)
    body
      .querySelectorAll(
        ".dropdown-toggle, [class*='caret'], .bi-caret-down, .bi-caret-down-fill, .bi-chevron-down"
      )
      .forEach((el) => {
        if (el.classList.contains("dropdown-toggle")) {
          el.classList.remove("dropdown-toggle");
        } else {
          el.remove();
        }
      });

    // чистим атрибуты и обезопашиваем ссылки
    body.querySelectorAll("*").forEach((el) => {
      Array.from(el.attributes).forEach((attr) => {
        const name = attr.name.toLowerCase();
        if (name.startsWith("on") || !KEEP_ATTRS.has(name)) {
          el.removeAttribute(attr.name);
        }
      });

      if (el.tagName === "A") {
        const href = el.getAttribute("href") || "";
        if (/^\s*javascript:/i.test(href)) {
          el.removeAttribute("href");
        }
        el.setAttribute("target", "_blank");
        el.setAttribute("rel", "noopener noreferrer");
      }
    });

    // разворачиваем <font> (оставляем содержимое)
    body.querySelectorAll("font").forEach((el) => {
      const parent = el.parentNode;
      if (!parent) {
        return;
      }
      while (el.firstChild) {
        parent.insertBefore(el.firstChild, el);
      }
      parent.removeChild(el);
    });

    // каретки-символы в текстовых узлах
    const walker = doc.createTreeWalker(body, NodeFilter.SHOW_TEXT);
    const textNodes: Text[] = [];
    while (walker.nextNode()) {
      textNodes.push(walker.currentNode as Text);
    }
    textNodes.forEach((node) => {
      const value = node.nodeValue || "";
      const cleaned = value.replace(CARET_GLYPHS, "");
      if (cleaned !== value) {
        node.nodeValue = cleaned;
      }
    });

    return body.innerHTML;
  } catch (e) {
    console.error("[sanitizeRichHtml] failed", e);
    return raw;
  }
}
